/**
 * AsthraBubbleSurface — the canonical "Asthra speaks" visual frame.
 *
 * One wrapper, one visual rule: the launch-film "clean card" — white
 * background, 1px hairline border on all four sides, 10px radius, a
 * soft lift shadow. The writer's violet bubble (MessageBubble) is the
 * contrasting surface, so the card itself carries no brand accent.
 *
 * Replaces three sites that previously each repeated this rule:
 *   - ``MessageBubble`` assistant variant (per-message bubble)
 *   - ``TimelineView``'s ``styles.greeting`` (idle-state greeting)
 *   - ``AgentMessageChips``'s root (conversational /draft prompts —
 *     this component itself was retired in Phase 6 when chip-based
 *     library/doctype selection moved to canonical surfaces;
 *     historical reference preserved for the rule-of-three context
 *     that justified this extraction)
 *
 * Each caller passes its own content; this wrapper owns ONLY the
 * frame + the optional "Asthra · …" label above it.
 *
 * SRP: pure presentation, frame only. No state, no interactivity,
 * no domain knowledge of messages or chips. The three callers each
 * keep ownership of their content.
 *
 * # DECISION: extracted at the third instance, not the second.
 * # WHY: per agentic-coding-principles "duplicate twice, extract on
 * #      the third". MessageBubble assistant and TimelineView
 * #      greeting were aligned in Phase 4 (two instances). The
 * #      AgentMessageChips divergence (caught when the writer asked
 * #      "do these messages follow the same UI behavior?") proved
 * #      the rule of three: three callers, identical visual contract,
 * #      one of which had drifted. Extracting now encodes the rule
 * #      in one file; future Asthra-speaks surfaces wrap this and
 * #      cannot drift by construction.
 * # DATE: 2026-04-26
 *
 * # DECISION: dropped the 2px brand-mid LEFT border + asymmetric corners
 * #   in favour of a uniform hairline card (supersedes 2026-04-26 frame).
 * # WHY: the launch film (screen 7) shows assistant replies as a plain
 * #      white card beside a FILLED violet user bubble. Once the writer's
 * #      bubble is the violet surface, a violet rule on the agent card
 * #      made both voices read as "brand" and blurred who is speaking.
 * # DATE: 2026-10-14
 *
 * # DECISION: ``label`` is optional, padding/margin are caller-chosen
 * #   via the surrounding container.
 * # WHY: the three callers have legitimately different padding (8/12,
 * #      16/14/14/16, 10/12/12/14) and margin contexts (inside chat
 * #      flow vs. standalone greeting card). Locking padding/margin
 * #      into the wrapper would force callers to fight defaults. The
 * #      wrapper owns the FRAME (border, radius, background); callers
 * #      own LAYOUT (padding, margin, max-width). Single Responsibility
 * #      preserved by drawing the boundary at "visual identity" rather
 * #      than "all-of-presentation".
 * # DATE: 2026-04-26
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { fonts, inkColors, surfaceColors, typeScale } from "../tokens";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AsthraBubbleSurfaceProps {
  /** Children render INSIDE the bubble frame. Caller controls
   *  internal layout (padding, max-width, content). */
  children: React.ReactNode;
  /** Optional mono label rendered ABOVE the bubble — e.g.
   *  "Asthra · 09:33", optionally with an inline intent chip
   *  (ReactNode so callers can compose label text + chip). Omit for
   *  surfaces where a timestamp is meaningless (the idle greeting
   *  renders without one). */
  label?: React.ReactNode;
  /** Optional className for the OUTER wrapper. Lets callers add
   *  margin / max-width / alignment without overriding frame
   *  styles. The wrapper's own className composes onto whatever
   *  the caller passes. */
  className?: string;
  /** Optional className for the INNER bubble div. Lets callers
   *  add padding / wrapping rules without overriding the frame. */
  bubbleClassName?: string;
  /** Optional ARIA role for the inner bubble. Default: omitted. */
  role?: string;
  /** Optional ARIA label for the inner bubble. */
  ariaLabel?: string;
  /** Optional controls rendered on the label row, right-aligned next
   *  to the label text (e.g. MessageBubble's Insert/Copy/Reply
   *  buttons). Rendered only when ``label`` or ``labelActions`` is
   *  set; callers own visibility gating (hover opacity etc.). */
  labelActions?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Styles — the canonical frame, in one place
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  outer: {
    // Wrapper holds the optional label; bubble sits inside.
    display: "flex",
    flexDirection: "column",
  },
  // Label row — label text left, optional caller controls right.
  // minHeight keeps the row from jumping when hover-gated controls
  // (opacity 0 → 1, but always in layout) sit beside a short label.
  labelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    marginBottom: "4px",
    paddingLeft: "2px",
  },
  // Small uppercase mono meta ("ASTHRA · 09:33") — the film's label
  // register. Uppercase is CSS-only so the DOM text stays as written.
  label: {
    display: "flex",
    alignItems: "center",
    fontSize: typeScale.label,
    color: inkColors.ink4,
    fontFamily: fonts.mono,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
  },
  bubble: {
    background: "var(--surface-raised)",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "10px",
    boxShadow: "0 1px 3px rgba(15, 10, 30, 0.05)",
    color: inkColors.ink2,
    fontFamily: fonts.body,
    lineHeight: typeScale.bodyLineHeight,
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AsthraBubbleSurface: React.FC<AsthraBubbleSurfaceProps> = ({
  children,
  label,
  className,
  bubbleClassName,
  role,
  ariaLabel,
  labelActions,
}) => {
  const styles = useStyles();
  const outerClass = className ? `${styles.outer} ${className}` : styles.outer;
  const bubbleClass = bubbleClassName ? `${styles.bubble} ${bubbleClassName}` : styles.bubble;
  return (
    <div className={outerClass}>
      {label || labelActions ? (
        <div className={styles.labelRow}>
          {label ? <div className={styles.label}>{label}</div> : null}
          {labelActions ?? null}
        </div>
      ) : null}
      <output className={bubbleClass} role={role} aria-label={ariaLabel}>
        {children}
      </output>
    </div>
  );
};

export default AsthraBubbleSurface;
